import classNames from 'classnames';
import { ForwardedRef, MouseEventHandler, ReactNode } from 'react';

import useElementSize from '../../hooks/useElementSize';

import { BasicButtonElement } from '../buttons/BasicButton';
import CardContainer, { CardContainerElement } from './CardContainer';

// import * as AppPropTypes from '../../lib/PropTypes';
import styles from '../../styles/cards/horizontal-card.module.css';

export type HorizontalCardElement =
    HTMLDivElement | HTMLSpanElement | HTMLLabelElement | BasicButtonElement;

export interface HorizontalCardProps {
    href?: string | null;
    external?: boolean;
    thumbnail?: ReactNode;
    thumbnailRatio?: number;
    maxThumbnailWidthRatio?: number;
    height?: number | null;
    responsive?: boolean;
    children?: ReactNode;
    inner?: ReactNode;
    brand?: string | null;
    presentation?: string;
    theme?: string | null;
    size?: string | null;
    isLabel?: boolean;
    labelFor?: string | null;
    className?: string | null;
    innerClassName?: string | null;
    thumbnailClassName?: string | null;
    labelClassName?: string | null;
    onClick?: MouseEventHandler<BasicButtonElement> | null;
}

const defaultThumbnailRatio = 4 / 3;

const HorizontalCard = ({
    href = null,
    external = false,
    thumbnail = null,
    thumbnailRatio = defaultThumbnailRatio,
    maxThumbnailWidthRatio = 0.3,
    height = null,
    responsive = false,
    children = null,
    inner = null,
    brand = null,
    presentation = 'horizontal',
    theme = null,
    size = null,
    isLabel = false,
    labelFor = null,
    className = null,
    innerClassName = null,
    thumbnailClassName = null,
    labelClassName = null,
    onClick = null,
    ref: containerRef = null,
}: HorizontalCardProps & { ref?: ForwardedRef<HorizontalCardElement> | null }) => {
    const {
        ref,
        height: elementHeight = 0,
        width: elementWidth = 0,
    } = useElementSize({
        disabled: !responsive,
    });

    let thumbnailWidth = null;
    if (responsive) {
        thumbnailWidth =
            elementHeight > 0 && elementWidth > 0
                ? Math.min(elementHeight * thumbnailRatio, elementWidth * maxThumbnailWidthRatio)
                : null;
    } else if (thumbnailRatio !== null && height !== null) {
        thumbnailWidth = height * thumbnailRatio;
    }

    const thumbnailStyle = {
        minWidth:
            thumbnailWidth !== null
                ? `var(--card-thumbnail-width, var(--card-thumbnail-size, ${thumbnailWidth}px))`
                : null,
        width:
            thumbnailWidth !== null
                ? `var(--card-thumbnail-width, var(--card-thumbnail-size, ${thumbnailWidth}px))`
                : null,
        minHeight:
            height !== null
                ? `var(--card-thumbnail-height, var(--card-thumbnail-size, ${height}px))`
                : null,
    };

    const labelStyle = {
        marginLeft:
            responsive && thumbnailWidth !== null
                ? `var(--card-thumbnail-width, var(--card-thumbnail-size, ${thumbnailWidth}px))`
                : null,
        minHeight: 'var(--card-label-min-height, 100%)',
    };

    const Element = href !== null || onClick !== null ? 'span' : 'div';

    const cardInner = (
        <Element
            className={classNames([styles.inner, innerClassName])}
            ref={ref as ForwardedRef<HTMLDivElement>}
        >
            {thumbnail !== null ? (
                <Element
                    className={classNames([
                        styles.thumbnail,
                        {
                            [thumbnailClassName]: thumbnailClassName !== null,
                        },
                    ])}
                    style={thumbnailStyle}
                >
                    {thumbnail}
                </Element>
            ) : null}
            <Element
                className={classNames([
                    styles.label,
                    {
                        [labelClassName]: labelClassName !== null,
                    },
                ])}
                style={labelStyle}
            >
                {children}
            </Element>
            {inner}
        </Element>
    );

    return (
        <CardContainer
            href={href}
            external={external}
            onClick={onClick}
            isLabel={isLabel}
            labelFor={labelFor}
            brand={brand}
            presentation={presentation}
            theme={theme}
            size={size}
            className={classNames([
                styles.container,
                {
                    [styles.responsive]: responsive,
                    [className]: className !== null,
                },
            ])}
            ref={containerRef as ForwardedRef<CardContainerElement>}
        >
            {cardInner}
        </CardContainer>
    );
};

export default HorizontalCard;
